import React from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate, Navigate } from 'react-router-dom';
import Survey from './components/Survey';
import SurveyData from './components/SurveyData';
import SurveyRenderer from './components/survey/SurveyRenderer';
import LoginPage from './components/LoginPage';
import GoogleCallback from './components/GoogleCallback';
import SurveyDashboard from './components/SurveyDashboard';
import GradeSelector from './components/GradeSelector';
import ProtectedRoute from './components/ProtectedRoute';
import StaffDashboard from './components/staff/StaffDashboard';
import AdminDashboard from './components/admin/AdminDashboard';
import ResponseRates from './components/admin/ResponseRates';
import QuestionBreakdowns from './components/admin/QuestionBreakdowns';
import ComparisonPage from './components/admin/ComparisonPage';
import SurveyTrends from './components/admin/SurveyTrends';
import DataExport from './components/admin/DataExport';
import { AuthProvider, useAuth, isAdminRole } from './contexts/AuthContext';
import { SurveyResponse } from './config/surveys/schema';
import './App.css';

// Protected route for admin-only pages
function ProtectedAdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdminRole(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Role-based home page - redirects based on user role
function RoleBasedHome() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Admin/Super-Admin -> Admin Dashboard
  if (isAdminRole(user.role)) {
    return <Navigate to="/admin" replace />;
  }

  // Staff -> Staff Dashboard
  if (user.role === 'staff') {
    return <StaffDashboard />;
  }

  // Student -> Student Dashboard
  return <SurveyDashboard />;
}

// Climate Survey Page - wraps SurveyRenderer with route params and auth
function ClimateSurveyPage() {
  const { surveyId } = useParams<{ surveyId: string }>();
  const { token, checkAuth, completedSurveys, logout } = useAuth();
  const navigate = useNavigate();

  // Redirect if survey already completed
  const alreadyCompleted = surveyId && completedSurveys.includes(surveyId);

  React.useEffect(() => {
    if (alreadyCompleted) {
      navigate('/', { replace: true });
    }
  }, [alreadyCompleted, navigate]);

  const handleComplete = async (response: SurveyResponse) => {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Include auth token if available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch('/api/climate-survey/submit', {
        method: 'POST',
        headers,
        body: JSON.stringify(response),
      });

      if (!res.ok) {
        // Handle auth errors - redirect to login
        if (res.status === 401) {
          console.error('Session expired, redirecting to login');
          logout();
          navigate('/login', { replace: true });
          return;
        }
        // Handle duplicate submission
        if (res.status === 409) {
          console.log('Survey already completed');
          await checkAuth();
          navigate('/', { replace: true });
          return;
        }
        console.error('Failed to submit climate survey:', res.statusText);
      } else {
        console.log('Climate survey submitted successfully');
        // Refresh auth state to update completed surveys
        await checkAuth();
        // Navigate back to home/dashboard
        navigate('/', { replace: true });
      }
    } catch (error) {
      console.error('Error submitting climate survey:', error);
    }
  };

  if (!surveyId) {
    return <div>Survey ID required</div>;
  }

  // Show nothing while redirecting
  if (alreadyCompleted) {
    return null;
  }

  return (
    <SurveyRenderer
      surveyId={surveyId}
      onComplete={handleComplete}
    />
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Home - Role-based landing (requires login) */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <RoleBasedHome />
          </ProtectedRoute>
        }
      />

      {/* Auth routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/google/callback" element={<GoogleCallback />} />

      {/* Protected routes */}
      <Route
        path="/select-grade"
        element={
          <ProtectedRoute>
            <GradeSelector />
          </ProtectedRoute>
        }
      />
      <Route
        path="/survey/:surveyId"
        element={
          <ProtectedRoute>
            <ClimateSurveyPage />
          </ProtectedRoute>
        }
      />

      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <ProtectedAdminRoute>
            <AdminDashboard />
          </ProtectedAdminRoute>
        }
      >
        <Route path="responses" element={<ResponseRates />} />
        <Route path="questions" element={<QuestionBreakdowns />} />
        <Route path="comparison" element={<ComparisonPage />} />
        <Route path="trends" element={<SurveyTrends />} />
        <Route path="export" element={<DataExport />} />
      </Route>

      {/* Legacy calendar survey (moved from root) */}
      <Route path="/calendar" element={<Survey />} />
      <Route path="/data" element={<SurveyData />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <Router>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </Router>
    </div>
  );
}

export default App;
